export type SurveyResponseTemplate =
  | 'question_with_options'
  | 'interpret_graph'
  | 'ordered_frequencies'
  | 'compare_options'
  | 'reflection'
  | 'labeled_short'
  | 'writing_lines';

export interface SurveyResponseField {
  id: string;
  label: string;
  lines: number;
  kind: 'text' | 'number';
}

export interface SurveyResponsePlan {
  template: SurveyResponseTemplate;
  fields: SurveyResponseField[];
  lineCount: number;
}

const PURPOSE_TEMPLATE: Record<string, SurveyResponseTemplate> = {
  survey_design: 'question_with_options',
  survey_interpret: 'interpret_graph',
  survey_frequency_calc: 'ordered_frequencies',
  survey_compare: 'compare_options',
  survey_reflect: 'reflection',
  survey_sample_limits: 'labeled_short',
};

export function inferSurveyPurposeFromText(...parts: Array<string | undefined>): string {
  const blob = parts
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const slotMatch = blob.match(
    /survey_(design|collect|chart|interpret|frequency_calc|compare|reflect|sample_limits)/,
  );
  if (slotMatch) return `survey_${slotMatch[1]}`;

  if (/dise[ñn]o.*pregunta|pregunta.*encuesta/.test(blob)) return 'survey_design';
  if (/interpretaci[oó]n|interpreta.*resultado|m[aá]s votos|menos votos/.test(blob)) {
    return 'survey_interpret';
  }
  if (/frecuencia|orden.*datos|mayor a menor/.test(blob)) return 'survey_frequency_calc';
  if (/comparaci[oó]n|compara.*opci|diferencia.*votos/.test(blob)) return 'survey_compare';
  if (/limitaciones.*muestra|piensa en la muestra|solo.*curso/.test(blob)) {
    return 'survey_sample_limits';
  }
  if (/reflexi|qu[eé] aprendiste/.test(blob)) return 'survey_reflect';

  return '';
}

function stripPracticePrefix(raw: string): string {
  return String(raw || '')
    .replace(/^pr[aá]ctica\s*:\s*/i, '')
    .trim();
}

function extractQuestionsFromPrompt(prompt: string): string[] {
  const value = String(prompt || '').trim();
  if (!value) return [];

  const byNumber = [...value.matchAll(/(?:^|\n)\s*\d+[.)]\s*([^?\n]+\?)/g)].map((m) => m[1].trim());
  if (byNumber.length >= 2) return byNumber;

  const byQuestion = [...value.matchAll(/([¿][^?\n]+\?)/g)].map((m) => m[1].trim());
  if (byQuestion.length >= 2) return byQuestion;

  return [];
}

function inferTemplateFromLabel(label: string): SurveyResponseTemplate | null {
  const purpose = inferSurveyPurposeFromText(label);
  if (purpose && PURPOSE_TEMPLATE[purpose]) return PURPOSE_TEMPLATE[purpose];

  const p = stripPracticePrefix(label).toLowerCase();
  if (/dise[ñn]o.*pregunta|pregunta.*encuesta/.test(p)) return 'question_with_options';
  if (/interpretaci[oó]n|interpreta.*resultado|analisis de datos|análisis de datos/.test(p)) {
    return 'interpret_graph';
  }
  if (/frecuencia|orden.*datos|c[aá]lculo.*frecuencia/.test(p)) return 'ordered_frequencies';
  if (/comparaci[oó]n|compara.*opci/.test(p)) return 'compare_options';
  if (/limitaciones|muestra|piensa en la muestra/.test(p)) return 'labeled_short';
  if (/reflexi|qu[eé] aprendiste/.test(p)) return 'reflection';
  return null;
}

function inferTemplateFromPrompt(prompt: string): SurveyResponseTemplate {
  const p = String(prompt || '').toLowerCase();

  if (/dise[ñn]o.*pregunta|pregunta.*encuesta|4 opciones/.test(p)) {
    return 'question_with_options';
  }
  if (
    (/m[aá]s votos|mayor cantidad|m[aá]s elegid|mayor frecuencia/.test(p) &&
      /menos votos|menor cantidad|menor frecuencia|menos elegid/.test(p)) ||
    (/gr[aá]fico|observa tu|analiz.*result|interpret.*result/.test(p) &&
      /conclusi[oó]n|m[aá]s|menos|preferencia/.test(p)) ||
    (/m[aá]s votos|menos votos/.test(p) && /gr[aá]fico|resultado|encuesta/.test(p))
  ) {
    return 'interpret_graph';
  }
  if (
    /orden.*mayor|mayor a menor|de mayor a menor|frecuencias? de cada|n[uú]meros en orden|calcul.*frecuencia|cuenta.*votos/.test(
      p,
    )
  ) {
    return 'ordered_frequencies';
  }
  if (
    /dos opciones|diferencia entre|cu[aá]ntos votos m[aá]s|calcula la diferencia|separan|compar.*opci/.test(
      p,
    )
  ) {
    return 'compare_options';
  }
  if (/limitaciones.*muestra|encuestar solo|otra muestra|piensa en la muestra/.test(p)) {
    return 'labeled_short';
  }
  if (/reflexi|qu[eé] aprendiste|qu[eé] cambiar[ií]as/.test(p)) {
    return extractQuestionsFromPrompt(prompt).length >= 2 ? 'labeled_short' : 'reflection';
  }

  if (extractQuestionsFromPrompt(prompt).length >= 2) return 'labeled_short';

  return 'writing_lines';
}

function fieldsForTemplate(template: SurveyResponseTemplate, prompt: string): SurveyResponseField[] {
  const questions = extractQuestionsFromPrompt(prompt);

  switch (template) {
    case 'interpret_graph':
      if (questions.length >= 2) {
        return questions.map((label, index) => ({
          id: `q${index + 1}`,
          label,
          lines: index === questions.length - 1 ? 2 : 1,
          kind: 'text' as const,
        }));
      }
      return [
        { id: 'most', label: 'Opción con más votos', lines: 1, kind: 'text' },
        { id: 'least', label: 'Opción con menos votos', lines: 1, kind: 'text' },
        { id: 'conclusion', label: 'Conclusión sobre las preferencias del curso', lines: 2, kind: 'text' },
      ];
    case 'ordered_frequencies':
      return [
        { id: 'rank1', label: '1° (mayor)', lines: 1, kind: 'number' },
        { id: 'rank2', label: '2°', lines: 1, kind: 'number' },
        { id: 'rank3', label: '3°', lines: 1, kind: 'number' },
        { id: 'rank4', label: '4° (menor)', lines: 1, kind: 'number' },
      ];
    case 'compare_options':
      return [
        { id: 'optionA', label: 'Opción A', lines: 1, kind: 'text' },
        { id: 'optionB', label: 'Opción B', lines: 1, kind: 'text' },
        { id: 'difference', label: 'Diferencia (en votos)', lines: 1, kind: 'number' },
        { id: 'explain', label: 'Qué significa esta diferencia', lines: 2, kind: 'text' },
      ];
    case 'reflection':
      return [{ id: 'reflection', label: 'Tu respuesta', lines: 4, kind: 'text' }];
    case 'labeled_short':
      return questions.map((label, index) => ({
        id: `q${index + 1}`,
        label,
        lines: /explica|describe|justifica|conclusi/i.test(label) ? 2 : 1,
        kind: 'text' as const,
      }));
    default:
      return [];
  }
}

function lineCountForTemplate(template: SurveyResponseTemplate, fields: SurveyResponseField[]): number {
  if (template === 'writing_lines') return 0;
  if (fields.length) return fields.reduce((sum, field) => sum + field.lines, 0);
  return 4;
}

function inferWritingLineCount(prompt: string): number {
  const questions = (prompt.match(/\?/g) || []).length;
  if (questions >= 3) return questions + 2;
  if (questions === 2) return 4;
  if (/reflexi|explica|describe|justifica/i.test(prompt)) return 5;
  if (/calcula|compara|ordena/i.test(prompt)) return 4;
  return 4;
}

export function resolveSurveyResponsePlan(
  block: {
    mode?: string;
    _blueprintPurpose?: string;
    label?: string;
    payload?: Record<string, unknown>;
  },
  prompt = '',
): SurveyResponsePlan {
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const inferredPurpose = inferSurveyPurposeFromText(
    block._blueprintPurpose,
    block.label,
    payload.title,
    payload.printTitle,
    prompt,
  );
  const purpose = String(block._blueprintPurpose || inferredPurpose || '');
  const labelBlob = [block.label, payload.title, payload.printTitle]
    .map((value) => stripPracticePrefix(String(value || '')))
    .filter(Boolean)
    .join(' ');
  const explicit = String(payload.surveyResponseTemplate || '') as SurveyResponseTemplate;
  const combined = `${prompt} ${labelBlob}`.trim();

  let template: SurveyResponseTemplate =
    explicit && explicit !== 'writing_lines'
      ? explicit
      : PURPOSE_TEMPLATE[purpose] ||
        inferTemplateFromLabel(labelBlob) ||
        inferTemplateFromPrompt(combined || prompt);

  if (template === 'question_with_options' && String(block.mode || '') !== 'desarrollo') {
    template = 'writing_lines';
  }

  if (template === 'reflection') {
    const questions = extractQuestionsFromPrompt(prompt);
    if (questions.length >= 2) template = 'labeled_short';
  }

  if (
    template === 'writing_lines' &&
    purpose &&
    PURPOSE_TEMPLATE[purpose] &&
    PURPOSE_TEMPLATE[purpose] !== 'writing_lines'
  ) {
    template = PURPOSE_TEMPLATE[purpose];
  }

  const fields = fieldsForTemplate(template, prompt);
  const lineCount =
    template === 'writing_lines' ? inferWritingLineCount(prompt) : lineCountForTemplate(template, fields);

  return { template, fields, lineCount };
}

export const SURVEY_RESPONSE_TEMPLATE_BY_PURPOSE = PURPOSE_TEMPLATE;
